import { useState } from "react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Check, X, Baby } from "lucide-react";
import { submitRsvp } from "@/lib/api";
import ConfettiBurst from "@/components/ConfettiBurst";

const CONFETTI_THEMES = new Set(["xv_anos", "cumpleanos", "otros"]);

export default function RSVPForm({ invitation, preview = false }) {
  const [name, setName] = useState("");
  const [attending, setAttending] = useState(null);
  const [count, setCount] = useState(1);
  const [msg, setMsg] = useState("");
  const [prediction, setPrediction] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);

  const isBabyReveal = invitation.theme === "baby_reveal" && invitation.reveal_prediction_enabled;
  const shouldConfetti = CONFETTI_THEMES.has(invitation.theme) || invitation.theme === "baby_reveal";

  const submit = async () => {
    if (preview) {
      toast.info("Vista previa: las confirmaciones solo se guardan en la invitación real");
      return;
    }
    if (!name.trim()) {
      toast.error("Escribe tu nombre");
      return;
    }
    if (attending === null) {
      toast.error("Confirma si asistirás");
      return;
    }
    try {
      setLoading(true);
      await submitRsvp(invitation.id, {
        guest_name: name.trim(),
        attending,
        guests_count: Number(count) || 1,
        message: msg.trim(),
        prediction: isBabyReveal ? prediction : null,
      });
      setSubmitted(true);
      if (attending && shouldConfetti) setShowConfetti(true);
      toast.success("¡Confirmación enviada!");

      if (invitation.host_whatsapp) {
        const text = encodeURIComponent(
          `Hola! Soy ${name}. ${attending ? "Confirmo mi asistencia" : "No podré asistir"} a ${invitation.title}${count > 1 ? ` (somos ${count})` : ""}${msg ? `. ${msg}` : ""}${prediction ? `. Predigo: ${prediction === "boy" ? "es niño 💙" : "es niña 💗"}` : ""}`
        );
        const phone = invitation.host_whatsapp.replace(/[^\d]/g, "");
        setTimeout(() => {
          window.open(`https://wa.me/${phone}?text=${text}`, "_blank");
        }, 900);
      }
    } catch (e) {
      toast.error("No se pudo enviar. Intenta de nuevo.");
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <>
        <ConfettiBurst active={showConfetti} />
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="glass rounded-3xl p-8 md:p-10 text-center max-w-md mx-auto"
          data-testid="rsvp-success"
        >
          <div className="w-14 h-14 rounded-full bg-warmgold/20 grid place-items-center mx-auto">
            <Check size={26} className="text-warmgold" />
          </div>
          <h3 className="headline text-3xl text-ink mt-5">¡Gracias, {name}!</h3>
          <p className="text-inksoft mt-3">Tu confirmación fue registrada. Te esperamos con muchísimo cariño.</p>
          <p className="script text-warmgold text-2xl mt-4">Con amor</p>
        </motion.div>
      </>
    );
  }

  return (
    <div className="max-w-md mx-auto">
      <div className="glass rounded-3xl p-6 md:p-8">
        <label className="block">
          <span className="text-xs tracking-widest uppercase text-inksoft">Tu nombre</span>
          <input
            data-testid="rsvp-name-input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Ana García"
            className="mt-2 w-full bg-transparent border-b border-warmgold/40 focus:border-warmgold focus:outline-none py-2 text-ink placeholder:text-inksoft/40"
          />
        </label>

        <div className="mt-6">
          <span className="text-xs tracking-widest uppercase text-inksoft">¿Nos acompañas?</span>
          <div className="mt-3 grid grid-cols-2 gap-3">
            <button
              onClick={() => setAttending(true)}
              data-testid="rsvp-yes-btn"
              className={`rounded-full py-3 text-sm font-medium transition-all border ${attending === true ? "bg-warmgold text-white border-warmgold shadow-md" : "bg-white/50 text-ink border-warmgold/30 hover:border-warmgold"}`}
            >
              <Check size={14} className="inline mr-1" /> Sí, ahí estaré
            </button>
            <button
              onClick={() => setAttending(false)}
              data-testid="rsvp-no-btn"
              className={`rounded-full py-3 text-sm font-medium transition-all border ${attending === false ? "bg-ink text-cream border-ink" : "bg-white/50 text-ink border-warmgold/30 hover:border-ink"}`}
            >
              <X size={14} className="inline mr-1" /> No podré
            </button>
          </div>
        </div>

        {attending === true && (
          <label className="block mt-6">
            <span className="text-xs tracking-widest uppercase text-inksoft">Somos</span>
            <input
              data-testid="rsvp-count-input"
              type="number"
              min={1}
              max={20}
              value={count}
              onChange={(e) => setCount(e.target.value)}
              className="mt-2 w-full bg-transparent border-b border-warmgold/40 focus:border-warmgold focus:outline-none py-2 text-ink"
            />
          </label>
        )}

        {isBabyReveal && attending === true && (
          <div className="mt-6">
            <span className="text-xs tracking-widest uppercase text-inksoft flex items-center gap-1"><Baby size={12} /> Tu predicción</span>
            <div className="mt-3 grid grid-cols-2 gap-3">
              <button
                onClick={() => setPrediction("girl")}
                data-testid="rsvp-predict-girl"
                className={`rounded-full py-3 text-sm transition-all border ${prediction === "girl" ? "bg-dustyrose text-white border-dustyrose" : "bg-white/50 border-dustyrose/40 text-ink hover:border-dustyrose"}`}
              >
                <span className="emoji-2x">💗</span> Es niña
              </button>
              <button
                onClick={() => setPrediction("boy")}
                data-testid="rsvp-predict-boy"
                className={`rounded-full py-3 text-sm transition-all border ${prediction === "boy" ? "bg-sage text-white border-sage" : "bg-white/50 border-sage/40 text-ink hover:border-sage"}`}
              >
                <span className="emoji-2x">💙</span> Es niño
              </button>
            </div>
          </div>
        )}

        <label className="block mt-6">
          <span className="text-xs tracking-widest uppercase text-inksoft">Mensaje (opcional)</span>
          <textarea
            data-testid="rsvp-message-input"
            value={msg}
            onChange={(e) => setMsg(e.target.value)}
            placeholder="Con muchísimo cariño..."
            rows={2}
            className="mt-2 w-full bg-transparent border-b border-warmgold/40 focus:border-warmgold focus:outline-none py-2 text-ink placeholder:text-inksoft/40 resize-none"
          />
        </label>

        <button
          onClick={submit}
          disabled={loading}
          className="btn-primary w-full mt-8 disabled:opacity-60"
          data-testid="rsvp-submit-btn"
        >
          {loading ? "Enviando..." : "Confirmar asistencia"}
        </button>

        {invitation.host_whatsapp && (
          <p className="text-center text-xs text-inksoft mt-3">Al confirmar te llevaremos a WhatsApp para avisar a los anfitriones.</p>
        )}
        <p className="text-center text-[11px] text-inksoft/80 mt-3 leading-relaxed">
          Tu respuesta solo la verán los anfitriones de este evento.{" "}
          <a
            href={`${process.env.REACT_APP_BACKEND_URL}/api/legal/privacidad.pdf`}
            target="_blank"
            rel="noreferrer"
            className="underline underline-offset-2 hover:text-ink transition-colors"
            data-testid="rsvp-privacy-link"
          >
            Aviso de privacidad
          </a>
        </p>
      </div>
    </div>
  );
}
